import { useEffect } from 'react';
import { Modal, Stack, MultiSelect, Switch, TagsInput, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { coachesApi } from '../../../services/api/coaches';
import type { Coach, UpdateCoachRequest } from '../../../types';
import { INDUSTRY_OPTIONS, PREDEFINED_EXPERTISE, CERTIFICATION_OPTIONS } from '../../../config/coachConfig';

interface EditProfessionalModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditProfessionalModal({
  opened,
  onClose,
  coach,
  onSaved,
}: EditProfessionalModalProps) {
  const form = useForm<UpdateCoachRequest>({
    initialValues: {
      industry_experience: [],
      facilitator_experience: false,
      certifications: [],
      expertise_areas: [],
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        industry_experience: coach.industry_experience || [],
        facilitator_experience: coach.facilitator_experience || false,
        certifications: coach.certifications || [],
        expertise_areas: coach.expertise_areas || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when modal opens with new coach data
  }, [opened, coach]);

  const handleSubmit = async (values: UpdateCoachRequest) => {
    try {
      const updatedCoach = await coachesApi.update(coach.id, values);
      notifications.show({
        title: 'Success',
        message: 'Professional background updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to update professional background',
        color: 'red',
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Professional Background" size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <MultiSelect
            label="Industry Experience"
            placeholder="Select industries"
            data={INDUSTRY_OPTIONS.map((i) => ({ value: i, label: i }))}
            searchable
            {...form.getInputProps('industry_experience')}
          />
          <Switch
            label="Has facilitator experience"
            description="Can facilitate workshops and group sessions"
            {...form.getInputProps('facilitator_experience', { type: 'checkbox' })}
          />
          <MultiSelect
            label="Certifications"
            placeholder="Select certifications"
            data={CERTIFICATION_OPTIONS.map((c) => ({ value: c, label: c }))}
            {...form.getInputProps('certifications')}
          />
          <TagsInput
            label="Expertise Areas"
            placeholder="Add expertise areas"
            description="Type and press Enter to add custom areas"
            data={PREDEFINED_EXPERTISE.map((e) => e)}
            {...form.getInputProps('expertise_areas')}
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
